import { ALLOWED_FILE_EXTENSIONS, MAX_UPLOAD_BYTES } from '@qualicloud/contracts';
import { useCallback, useId, useRef, useState, type DragEvent } from 'react';
import { Button } from './button';
import { IconClose, IconFile, IconUpload } from './icons';
import { cn, focusRing } from './utils';

export interface FileValidationResult {
  readonly ok: boolean;
  /** Código estável — espelha `ErrorCode` de mídia para a mensagem coincidir. */
  readonly code?: 'FILE_TOO_LARGE' | 'FILE_EXTENSION_NOT_ALLOWED' | 'FILE_EMPTY';
  readonly message?: string;
}

/**
 * Validação no CLIENTE, antes de subir.
 *
 * Os limites vêm de `@qualicloud/contracts` (`MAX_UPLOAD_BYTES`,
 * `ALLOWED_FILE_EXTENSIONS`) — os mesmos valores que o MEDIA aplica. Isto é
 * conveniência, NÃO controle: o servidor revalida tamanho, extensão, MIME real
 * por magic bytes e antivírus. Validar aqui evita fazer o usuário esperar 20 MB
 * subirem para descobrir que a extensão não é aceita.
 */
export function validateFile(file: File): FileValidationResult {
  if (file.size === 0) {
    return { ok: false, code: 'FILE_EMPTY', message: 'O arquivo está vazio.' };
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    return {
      ok: false,
      code: 'FILE_TOO_LARGE',
      message: `O arquivo tem ${formatBytes(file.size)}. O limite é ${formatBytes(MAX_UPLOAD_BYTES)}.`,
    };
  }
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (!(ALLOWED_FILE_EXTENSIONS as readonly string[]).includes(extension)) {
    return {
      ok: false,
      code: 'FILE_EXTENSION_NOT_ALLOWED',
      message: `Extensão “.${extension}” não permitida. Aceitas: ${ALLOWED_FILE_EXTENSIONS.join(', ')}.`,
    };
  }
  return { ok: true };
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1).replace('.', ',')} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

export interface FileDropzoneProps {
  readonly file: File | null;
  readonly onFileChange: (file: File | null) => void;
  readonly disabled?: boolean;
  /** 0–100. `null` quando não há envio em curso. */
  readonly progress?: number | null;
  readonly error?: string | undefined;
  readonly className?: string;
  readonly label?: string;
  readonly hint?: string;
}

/**
 * Área de upload com arrastar-e-soltar.
 *
 * ACESSIBILIDADE: arrastar e soltar NUNCA é o único caminho (WCAG 2.5.7). O
 * `<input type="file">` real fica sob a área e é acionável por teclado; a área
 * visual é apenas um rótulo estilizado. O resultado de cada validação vai para
 * um `role="alert"`, e o progresso para um `role="progressbar"` com
 * `aria-valuenow` — barra de progresso muda o valor, e sem isso o leitor de
 * tela não sabe se o envio avançou.
 */
export function FileDropzone({
  file,
  onFileChange,
  disabled = false,
  progress = null,
  error,
  className,
  label = 'Arquivo do documento',
  hint,
}: FileDropzoneProps) {
  const inputId = useId();
  const errorId = `${inputId}-error`;
  const hintId = `${inputId}-hint`;
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const accept = ALLOWED_FILE_EXTENSIONS.map((ext) => `.${ext}`).join(',');
  const shownError = error ?? localError;

  const accept_file = useCallback(
    (candidate: File | undefined) => {
      if (!candidate) return;
      const result = validateFile(candidate);
      if (!result.ok) {
        setLocalError(result.message ?? 'Arquivo inválido.');
        onFileChange(null);
        return;
      }
      setLocalError(null);
      onFileChange(candidate);
    },
    [onFileChange],
  );

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    if (disabled) return;
    accept_file(event.dataTransfer.files[0]);
  };

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        className={cn(
          'relative rounded-lg border-2 border-dashed p-6 text-center transition-colors',
          dragging ? 'border-brand bg-brand-subtle' : 'border-border-control bg-surface-sunken',
          shownError && 'border-danger',
          disabled && 'opacity-55',
          'focus-within:ring-2 focus-within:ring-focus focus-within:ring-offset-2 focus-within:ring-offset-canvas',
        )}
      >
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          accept={accept}
          disabled={disabled}
          aria-describedby={[hint ? hintId : null, shownError ? errorId : null]
            .filter(Boolean)
            .join(' ')
            .trim() || undefined}
          aria-invalid={shownError ? true : undefined}
          onChange={(event) => accept_file(event.target.files?.[0])}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
        />

        <div className="pointer-events-none flex flex-col items-center gap-2">
          <span
            aria-hidden="true"
            className="flex h-11 w-11 items-center justify-center rounded-full bg-surface text-content-subtle"
          >
            <IconUpload size={20} />
          </span>
          <label htmlFor={inputId} className="text-sm font-medium text-content">
            {label}
          </label>
          <p className="text-sm text-content-muted">
            Arraste o arquivo aqui ou{' '}
            <span className="font-medium text-brand underline">clique para selecionar</span>
          </p>
          <p id={hintId} className="text-xs text-content-subtle">
            {hint ??
              `Até ${formatBytes(MAX_UPLOAD_BYTES)}. Formatos aceitos: ${ALLOWED_FILE_EXTENSIONS.join(', ')}.`}
          </p>
        </div>
      </div>

      {file ? (
        <div className="flex items-center gap-3 rounded-md border border-border-control bg-surface px-3 py-2.5">
          <IconFile size={18} className="shrink-0 text-content-subtle" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-content">{file.name}</p>
            <p className="text-xs text-content-subtle qc-tabular">{formatBytes(file.size)}</p>
          </div>
          {progress !== null ? (
            <div className="w-40 shrink-0">
              <div
                role="progressbar"
                aria-valuenow={Math.round(progress)}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`Enviando ${file.name}`}
                className="h-2 w-full overflow-hidden rounded-full bg-surface-sunken"
              >
                <div
                  className="h-full rounded-full bg-brand transition-[width] duration-200"
                  style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
                />
              </div>
              <p aria-live="polite" className="mt-1 text-right text-2xs text-content-subtle qc-tabular">
                {Math.round(progress)}% enviado
              </p>
            </div>
          ) : (
            <Button
              size="icon"
              variant="ghost"
              iconLabel={`Remover o arquivo ${file.name}`}
              disabled={disabled}
              onClick={() => {
                onFileChange(null);
                setLocalError(null);
                if (inputRef.current) inputRef.current.value = '';
              }}
            >
              <IconClose size={16} />
            </Button>
          )}
        </div>
      ) : null}

      {shownError ? (
        <p id={errorId} role="alert" className={cn('text-xs font-medium text-danger-content', focusRing)}>
          {shownError}
        </p>
      ) : null}
    </div>
  );
}
